import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiClient, type ApiConfig } from '@/api/client';
import { createEndpoints, type Endpoints } from '@/api/endpoints';
import { ToastStackProvider } from '@/components/arc/toast-stack/toast-stack';
import { JobsStoreProvider } from '@/api/ws';

export interface MomentContextValue {
  client: ApiClient;
  api: Endpoints;
  config: ApiConfig;
  timezone: string;
}

const MomentContext = createContext<MomentContextValue | null>(null);

export interface MomentProviderProps {
  config?: ApiConfig;
  /** Bring your own QueryClient (the host panel may already have one). */
  queryClient?: QueryClient;
  children: ReactNode;
}

function defaultQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: (count, err) => {
          const status = (err as { status?: number }).status;
          if (status && status >= 400 && status < 500) return false;
          return count < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Root provider: react-query client + API configuration + toasts + job push store.
 * Wrap the host route tree (or the standalone app) once.
 */
export function MomentProvider({ config = {}, queryClient, children }: MomentProviderProps) {
  const qc = useMemo(() => queryClient ?? defaultQueryClient(), [queryClient]);
  const value = useMemo<MomentContextValue>(() => {
    const client = new ApiClient(config);
    return { client, api: createEndpoints(client), config, timezone: client.timezone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.baseUrl, config.orgId, config.wsUrl, config.timezone, config.getAuthHeaders]);

  return (
    <QueryClientProvider client={qc}>
      <MomentContext.Provider value={value}>
        <ToastStackProvider>
          <JobsStoreProvider>{children}</JobsStoreProvider>
        </ToastStackProvider>
      </MomentContext.Provider>
    </QueryClientProvider>
  );
}

export function useMoment(): MomentContextValue {
  const ctx = useContext(MomentContext);
  if (!ctx) throw new Error('useMoment must be used inside <MomentProvider>');
  return ctx;
}

export function useApi(): Endpoints {
  return useMoment().api;
}

export function useTimezone(): string {
  return useMoment().timezone;
}
